import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { listConnections } from '../connectors/api';
import { updateAgent, type Agent } from './api';

type TreasuryOutput = {
  currency: string;
  network: string;
  address: string;
  memo?: string;
};

type TreasuryConfig = {
  bit2me_connection_id?: string;
  allowed_currencies?: string[];
  max_per_tx_eur?: string;
  max_per_day_eur?: string;
  allowed_outputs?: Record<string, TreasuryOutput>;
};

export function TreasuryConfigForm({ orgId, agent }: { orgId: string; agent: Agent }) {
  const qc = useQueryClient();
  const cfg = (agent.config as TreasuryConfig) ?? {};

  // Only Bit2Me connections can power a Treasury agent. The select
  // filters to those server-side data; mismatching choices are rejected
  // at runtime, so showing the filtered list is enough to keep the UI
  // honest.
  const connections = useQuery({
    queryKey: ['connections', orgId],
    queryFn: () => listConnections(orgId),
    enabled: !!orgId,
  });
  const bit2meConnections =
    (connections.data ?? []).filter((c) => c.definition_slug === 'bit2me');

  const [connectionId, setConnectionId] = useState(cfg.bit2me_connection_id ?? '');
  const [currencies, setCurrencies] = useState((cfg.allowed_currencies ?? []).join(', '));
  const [maxTx, setMaxTx] = useState(cfg.max_per_tx_eur ?? '');
  const [maxDay, setMaxDay] = useState(cfg.max_per_day_eur ?? '');
  const [outputs, setOutputs] = useState<Record<string, TreasuryOutput>>(cfg.allowed_outputs ?? {});
  const [draft, setDraft] = useState<{ label: string } & TreasuryOutput>({
    label: '',
    currency: '',
    network: '',
    address: '',
    memo: '',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setConnectionId(cfg.bit2me_connection_id ?? '');
    setCurrencies((cfg.allowed_currencies ?? []).join(', '));
    setMaxTx(cfg.max_per_tx_eur ?? '');
    setMaxDay(cfg.max_per_day_eur ?? '');
    setOutputs(cfg.allowed_outputs ?? {});
    setSaved(false);
  }, [agent.id]);

  const save = useMutation({
    mutationFn: () => {
      const next: TreasuryConfig = {
        bit2me_connection_id: connectionId,
        allowed_currencies: currencies
          .split(',')
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean),
        max_per_tx_eur: maxTx.trim(),
        max_per_day_eur: maxDay.trim(),
        allowed_outputs: outputs,
      };
      return updateAgent(orgId, agent.id, { config: next });
    },
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['agent', orgId, agent.id] });
      setTimeout(() => setSaved(false), 3000);
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  function addOutput() {
    if (!draft.label || !draft.currency || !draft.network || !draft.address) return;
    setOutputs((prev) => ({
      ...prev,
      [draft.label]: {
        currency: draft.currency.toLowerCase(),
        network: draft.network.toLowerCase(),
        address: draft.address,
        memo: draft.memo || undefined,
      },
    }));
    setDraft({ label: '', currency: '', network: '', address: '', memo: '' });
  }

  function removeOutput(label: string) {
    setOutputs((prev) => {
      const { [label]: _drop, ...rest } = prev;
      return rest;
    });
  }

  return (
    <Card>
      <CardHeader
        title="Treasury policy"
        description="Caps and allow-lists for Bit2Me buy / sell / withdraw. The runner enforces them server-side — the LLM cannot exceed them even if asked."
      />
      <CardBody>
        <form onSubmit={handleSubmit} className="space-y-5">
          <Section
            title="Bit2Me connection"
            hint="Pick the org's Bit2Me connection this agent will use to sign requests. Add one in Connectors → Bit2Me first."
          >
            {bit2meConnections.length === 0 ? (
              <p className="text-xs text-amber-700">
                No Bit2Me connections yet. Add one under Connectors.
              </p>
            ) : (
              <select
                value={connectionId}
                onChange={(e) => setConnectionId(e.target.value)}
                className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm"
              >
                <option value="">— pick a connection —</option>
                {bit2meConnections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.account_label}
                  </option>
                ))}
              </select>
            )}
          </Section>

          <Section
            title="Allowed currencies"
            hint="Comma-separated crypto symbols the agent can trade or withdraw (e.g. btc, eth, usdc). Always paired with EUR on the fiat side."
          >
            <input
              value={currencies}
              onChange={(e) => setCurrencies(e.target.value)}
              placeholder="btc, eth, usdc"
              className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm"
            />
          </Section>

          <div className="grid grid-cols-2 gap-3">
            <Section title="Max per tx (EUR)" hint="Per buy / sell / withdraw cap. Blank or 0 = no cap.">
              <input
                value={maxTx}
                onChange={(e) => setMaxTx(e.target.value)}
                placeholder="500"
                className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm"
              />
            </Section>
            <Section title="Max per day (EUR)" hint="Rolling 24h ceiling across all writes.">
              <input
                value={maxDay}
                onChange={(e) => setMaxDay(e.target.value)}
                placeholder="2000"
                className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm"
              />
            </Section>
          </div>

          <Section
            title="Allowed withdrawal destinations"
            hint="Whitelist of addresses the agent may withdraw to, keyed by an operator-friendly label. The LLM can only pass a label — never an arbitrary address."
          >
            {agent.wallet_id && (
              <p className="mb-2 rounded border border-ok/30 bg-ok/5 px-2 py-1.5 text-[11px] text-ink-secondary">
                <span className="font-mono uppercase text-ok">agent_wallet</span> is implicitly
                allowed because this agent has a wallet bound — the runner resolves it to the
                wallet's address at run time. No need to copy-paste.
              </p>
            )}
            <ul className="space-y-1">
              {Object.entries(outputs).map(([label, dest]) => (
                <li
                  key={label}
                  className="flex items-center justify-between gap-2 rounded border border-line-soft bg-surface-soft/40 px-2 py-1"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-ink-primary">{label}</p>
                    <p className="truncate font-mono text-[10px] text-ink-tertiary">
                      {dest.currency.toUpperCase()} · {dest.network} · {dest.address}
                      {dest.memo ? ` · memo ${dest.memo}` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeOutput(label)}
                    className="text-[11px] text-danger hover:underline"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-2 grid grid-cols-5 gap-2">
              <input
                value={draft.label}
                onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                placeholder="label"
                className="rounded border border-line-soft px-2 py-1 text-xs"
              />
              <input
                value={draft.currency}
                onChange={(e) => setDraft((d) => ({ ...d, currency: e.target.value }))}
                placeholder="usdc"
                className="rounded border border-line-soft px-2 py-1 text-xs"
              />
              <input
                value={draft.network}
                onChange={(e) => setDraft((d) => ({ ...d, network: e.target.value }))}
                placeholder="polygon"
                className="rounded border border-line-soft px-2 py-1 text-xs"
              />
              <input
                value={draft.address}
                onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
                placeholder="0x…"
                className="col-span-2 rounded border border-line-soft px-2 py-1 font-mono text-xs"
              />
            </div>
            <button
              type="button"
              onClick={addOutput}
              className="mt-2 rounded border border-line-soft px-3 py-1 text-xs text-ink-secondary hover:bg-surface-soft"
            >
              + Add destination
            </button>
          </Section>

          {save.error && (
            <p className="text-sm text-danger">{(save.error as Error).message || 'Could not save'}</p>
          )}
          <div className="flex items-center justify-end gap-3">
            {saved && <p className="font-mono text-xs text-ok">Saved</p>}
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink-primary">{title}</label>
      {hint && <p className="mt-0.5 text-xs text-ink-tertiary">{hint}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}
